import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const BASE = `${config.BASE_URL}/donor-control/donor-equipment-list`;


async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function messageForResponse(res, payload, fallback) {
  if (res.status >= 500) {
    return "Internal server error. Please try again later.";
  }
  const m = payload?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

function buildApiError(res, payload, fallback) {
  const error = new Error(messageForResponse(res, payload, fallback));
  if (payload && typeof payload === "object") {
    error.details = payload.details || {};
    error.payload = payload;
  }
  error.status = res.status;
  return error;
}

function toQuery(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== "") q.set(k, String(v));
  });
  return q.toString();
}

export const getDonorEquipmentDashboard = async (filters = {}) => {
  const q = toQuery(filters);
  const res = await authFetch(`${BASE}/dashboard${q ? `?${q}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to fetch dashboard");
  return data;
};

export const getDonorEquipmentList = async (filters = {}) => {
  const q = toQuery(filters);
  const res = await authFetch(`${BASE}${q ? `?${q}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to fetch equipment list");
  return data;
};

export const getDonorEquipmentLookups = async () => {
  const res = await authFetch(`${BASE}/lookups`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to fetch lookups");
  return data;
};

export const createDonorEquipment = async (body, csrfToken) => {
  const res = await authFetch(`${BASE}`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to create equipment");
  return data;
};

export const updateDonorEquipment = async (id, body, csrfToken) => {
  const res = await authFetch(`${BASE}/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to update equipment");
  return data;
};

export const deleteDonorEquipment = async (id, csrfToken) => {
  const res = await authFetch(`${BASE}/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to deactivate equipment");
  return data;
};

export const bulkSetEquipmentDonationVerified = async (ids, donationVerified, csrfToken) => {
  const res = await authFetch(`${BASE}/bulk-donation-verified`, {
    method: "PATCH",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({
      ids,
      donation_verified: donationVerified ? 1 : 0,
    }),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to update donation verification");
  return data;
};

export const getEquipmentAllocations = async (equipmentId) => {
  const q = toQuery({ equipment_id: equipmentId });
  const res = await authFetch(`${BASE}/allocations${q ? `?${q}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to fetch allocations");
  return data;
};

export const createEquipmentAllocation = async (body, csrfToken) => {
  const res = await authFetch(`${BASE}/allocations`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to create allocation");
  return data;
};

export const updateEquipmentAllocation = async (id, body, csrfToken) => {
  const res = await authFetch(`${BASE}/allocations/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to update allocation");
  return data;
};

export const deleteEquipmentAllocation = async (id, csrfToken) => {
  const res = await authFetch(`${BASE}/allocations/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to delete allocation");
  return data;
};

/* ================= EQUIPMENT MASTER TABLES ================= */
export const getEquipmentCategoryMasters = async ({ scope } = {}) => {
  const q = scope === "master" ? "?scope=master" : "";
  const res = await authFetch(`${BASE}/masters/categories${q}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to fetch equipment categories");
  return data;
};

export const createEquipmentCategoryMaster = async (body, csrfToken) => {
  const res = await authFetch(`${BASE}/masters/categories`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to create equipment category");
  return data;
};

export const updateEquipmentCategoryMaster = async (id, body, csrfToken) => {
  const res = await authFetch(`${BASE}/masters/categories/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to update equipment category");
  return data;
};

export const deleteEquipmentCategoryMaster = async (id, csrfToken) => {
  const res = await authFetch(`${BASE}/masters/categories/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to delete equipment category");
  return data;
};

export const getEquipmentConditionMasters = async ({ scope } = {}) => {
  const q = scope === "master" ? "?scope=master" : "";
  const res = await authFetch(`${BASE}/masters/conditions${q}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to fetch equipment conditions");
  return data;
};

export const createEquipmentConditionMaster = async (body, csrfToken) => {
  const res = await authFetch(`${BASE}/masters/conditions`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to create equipment condition");
  return data;
};

export const updateEquipmentConditionMaster = async (id, body, csrfToken) => {
  const res = await authFetch(`${BASE}/masters/conditions/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to update equipment condition");
  return data;
};

export const deleteEquipmentConditionMaster = async (id, csrfToken) => {
  const res = await authFetch(`${BASE}/masters/conditions/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to delete equipment condition");
  return data;
};

export const getEquipmentStatusMasters = async ({ scope } = {}) => {
  const q = scope === "master" ? "?scope=master" : "";
  const res = await authFetch(`${BASE}/masters/statuses${q}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to fetch equipment statuses");
  return data;
};

export const createEquipmentStatusMaster = async (body, csrfToken) => {
  const res = await authFetch(`${BASE}/masters/statuses`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to create equipment status");
  return data;
};

export const updateEquipmentStatusMaster = async (id, body, csrfToken) => {
  const res = await authFetch(`${BASE}/masters/statuses/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to update equipment status");
  return data;
};

export const deleteEquipmentStatusMaster = async (id, csrfToken) => {
  const res = await authFetch(`${BASE}/masters/statuses/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const data = await parseJson(res);
  if (!res.ok) throw buildApiError(res, data, "Failed to delete equipment status");
  return data;
};
